import { Button, StyledText } from 'UI';
import { CourseDto } from 'api/generated';
import { BookIcon } from 'assets/icons';
import { useNotifications } from 'hooks';
import { FC, HTMLAttributes } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';

interface TextbookProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
  course: CourseDto;
  onClick?: (course: CourseDto) => void;
  disabled?: boolean;
  isLastItem?: boolean;
}

const Textbook: FC<TextbookProps> = ({ course, onClick: _onClick, disabled, isLastItem, ...props }) => {
  const { colors } = useTheme();
  const { errorToast } = useNotifications();
  const textBookUrl = (course as any)?.textbookUrl;

  const handleTextbookClick = async () => {
    if (!course) return;

    // Allow all users (including trial) to access textbooks
    try {
      if (textBookUrl) {
        window.open(textBookUrl, '_blank');
      } else {
        errorToast('Textbook not available');
      }
    } catch (error) {
      errorToast('Failed to open textbook');
    }
  };

  return (
    <Root $isLastItem={isLastItem} {...props}>
      <Content>
        <CourseInfo>
          <CourseName>
            <CourseTitleText font="body_large_bold_16">{course?.name}</CourseTitleText>
          </CourseName>
          <CourseDescription>
            <CourseDescriptionText font="footnote_regular_12">{course?.description}</CourseDescriptionText>
          </CourseDescription>
        </CourseInfo>
        <Actions>
          <Button
            size="small"
            variant="primary"
            onClick={handleTextbookClick}
            disabled={disabled || !textBookUrl}
            iconComponent={<BookIcon color={colors.neutrals[11]} width={16} height={16} />}>
            Download Textbook
          </Button>
        </Actions>
      </Content>
    </Root>
  );
};

export default Textbook;

const Root = styled.div<{ $isLastItem?: boolean }>`
  ${({ theme: { colors }, $isLastItem }) => css`
    width: 100%;
    min-height: 80px;
    padding: 16px;
    background-color: transparent;
    border: 1px solid ${colors.neutrals[9]};
    border-radius: 8px;

    ${!$isLastItem &&
    css`
      margin-bottom: 8px;
    `}

    ${respondToWidth.sm`
      min-height: 72px;
      padding: 12px 16px;
    `}
  `}
`;

const Content = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 16px;
`;

const CourseInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 4px;
`;

const CourseName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CourseDescription = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CourseTitleText = styled(StyledText)`
  ${({ theme: { colors } }) => css`
    color: ${colors.neutrals[1]};
  `}
`;

const CourseDescriptionText = styled(StyledText)`
  ${({ theme: { colors } }) => css`
    color: ${colors.neutrals[4]};
  `}
`;
